
import { useState, useEffect, useRef } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    location: 'praha',
    message: '',
    items: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isVisible, setIsVisible] = useState(false);
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            // Postupně zobrazit karty
            setTimeout(() => setVisibleCards([0]), 200);
            setTimeout(() => setVisibleCards([0, 1]), 400);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const form = e.target as HTMLFormElement;
      const formDataToSend = new FormData(form);
      
      // Převod na application/x-www-form-urlencoded
      const urlEncodedData = new URLSearchParams();
      formDataToSend.forEach((value, key) => {
        urlEncodedData.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d5sjh2j99ei0h7kmajdg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          location: 'praha',
          message: '',
          items: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const openCart = () => {
    const cartButton = document.querySelector('[data-quick-cart-button]') as HTMLElement;
    if (cartButton) {
      cartButton.click();
    }
  };

  return (
    <section ref={sectionRef} id="kontakt" className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-[95%] sm:max-w-[90%] lg:max-w-[70%] mx-auto px-4 sm:px-6">
        <div
          className={`text-center mb-10 sm:mb-12 md:mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'
          }`}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
            Kontaktujte nás
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300">
            Jsme tu pro vás na dvou lokalitách
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 md:gap-12 mb-10 sm:mb-12 md:mb-16">
          {/* Praha */}
          <div
            className={`bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-xl p-6 sm:p-8 hover:shadow-2xl transition-all duration-700 ${
              visibleCards.includes(0)
                ? 'opacity-100 translate-x-0 rotate-0'
                : 'opacity-0 -translate-x-20 -rotate-3'
            }`}
          >
            <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div
                className={`w-12 h-12 sm:w-16 sm:h-16 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 delay-300 ${
                  visibleCards.includes(0) ? 'scale-100 rotate-0' : 'scale-0 rotate-180'
                }`}
              >
                <i className="ri-map-pin-line text-2xl sm:text-3xl text-teal-600 dark:text-teal-400"></i>
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">Praha</h3>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">Hlavní město a okolí</p>
              </div>
            </div>
            
            <div className="space-y-3 sm:space-y-4">
              <a href="tel:+420776166733" className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors group cursor-pointer">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center group-hover:bg-teal-100 dark:group-hover:bg-teal-900/50 transition-colors flex-shrink-0">
                  <i className="ri-phone-fill text-xl sm:text-2xl text-teal-600 dark:text-teal-400"></i>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Telefon</p>
                  <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">+420 776 166 733</p>
                </div>
              </a>

              <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-truck-line text-xl sm:text-2xl text-teal-600 dark:text-teal-400"></i>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Doprava</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">399 Kč za obě cesty</p>
                </div>
              </div>
            </div>
          </div>

          {/* Stará Boleslav */}
          <div
            className={`bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-xl p-6 sm:p-8 hover:shadow-2xl transition-all duration-700 ${
              visibleCards.includes(1)
                ? 'opacity-100 translate-x-0 rotate-0'
                : 'opacity-0 translate-x-20 rotate-3'
            }`}
          >
            <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div
                className={`w-12 h-12 sm:w-16 sm:h-16 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 delay-300 ${
                  visibleCards.includes(1) ? 'scale-100 rotate-0' : 'scale-0 rotate-180'
                }`}
              >
                <i className="ri-map-pin-line text-2xl sm:text-3xl text-teal-600 dark:text-teal-400"></i>
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">Stará Boleslav</h3>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">A okolí</p>
              </div>
            </div>
            
            <div className="space-y-3 sm:space-y-4">
              <a href="tel:+420603290573" className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors group cursor-pointer">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center group-hover:bg-teal-100 dark:group-hover:bg-teal-900/50 transition-colors flex-shrink-0">
                  <i className="ri-phone-fill text-xl sm:text-2xl text-teal-600 dark:text-teal-400"></i>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Telefon</p>
                  <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">+420 603 290 573</p>
                </div>
              </a>

              <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-xl sm:text-2xl text-teal-600 dark:text-teal-400"></i>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Rychlé vyřízení</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Individuální domluva</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
